import { useEffect, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api, { getApiError } from '../../lib/axios';
import { FileText, Check, X, Loader2, ChevronLeft, ChevronRight, Mail } from 'lucide-react';

interface Submission {
  id: string;
  email: string;
  domains: Array<{ domain?: string; price?: number }>;
  marketplace: string | null;
  status: string;
  created_at: string | null;
}

export default function AdminSubmissions() {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const perPage = 20;

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
      if (filter) params.set('status_filter', filter);
      const res = await api.get(`/api/submit/admin/list?${params}`);
      setSubmissions(res.data.submissions);
      setTotal(res.data.total);
    } catch (err: any) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSubmissions(); }, [page, filter]);

  const handleAction = async (id: string, status: 'approved' | 'rejected') => {
    setUpdatingId(id);
    try {
      await api.patch(`/api/submit/admin/${id}`, { status });
      setSubmissions(prev => prev.map(s => s.id === id ? { ...s, status } : s));
    } catch (err: any) {
      setError(getApiError(err));
    } finally {
      setUpdatingId(null);
    }
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
      approved: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      rejected: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${styles[status] || ''}`}>{status}</span>;
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Website Submissions</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{total} submissions</p>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2">
          {['', 'pending', 'approved', 'rejected'].map(f => (
            <button
              key={f}
              onClick={() => { setFilter(f); setPage(1); }}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium ${
                filter === f ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {f ? f.charAt(0).toUpperCase() + f.slice(1) : 'All'}
            </button>
          ))}
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{error}</div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-brand-600" /></div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No submissions</p>
          </div>
        ) : (
          <div className="space-y-4">
            {submissions.map(s => (
              <div key={s.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900 dark:text-white">{s.email}</span>
                      {statusBadge(s.status)}
                    </div>
                    <p className="text-xs text-gray-500">
                      {s.created_at ? new Date(s.created_at).toLocaleString() : ''}
                      {s.marketplace && ` · Marketplace: ${s.marketplace}`}
                    </p>
                  </div>
                  {s.status === 'pending' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAction(s.id, 'approved')}
                        disabled={updatingId === s.id}
                        className="px-3 py-1.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 text-sm font-medium rounded-lg flex items-center gap-1"
                      >
                        <Check className="w-3.5 h-3.5" /> Approve
                      </button>
                      <button
                        onClick={() => handleAction(s.id, 'rejected')}
                        disabled={updatingId === s.id}
                        className="px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 text-sm font-medium rounded-lg flex items-center gap-1"
                      >
                        <X className="w-3.5 h-3.5" /> Reject
                      </button>
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3">
                  <p className="text-xs font-medium text-gray-500 mb-2">{s.domains.length} domain(s) submitted:</p>
                  <div className="grid gap-1 text-sm">
                    {s.domains.slice(0, 10).map((d, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-gray-900 dark:text-white font-mono text-xs">{d.domain || '—'}</span>
                        {d.price && <span className="text-green-600 dark:text-green-400 text-xs font-medium">${d.price}</span>}
                      </div>
                    ))}
                    {s.domains.length > 10 && <p className="text-xs text-gray-400">... and {s.domains.length - 10} more</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
